import { RefObject, useEffect } from 'react';
import { CustomTileHostRegistry } from 'features/layout/tiles/custom-tile-host/registry';

type Geometry = {
  x: number;
  y: number;
  width: number;
  height: number;
};

type MeasuredHost = {
  host: HTMLElement;
  geometry: Geometry | null;
};

const toKey = (geometry: Geometry | null, interactive: boolean) =>
  geometry
    ? `${geometry.x},${geometry.y},${geometry.width},${geometry.height},${interactive}`
    : null;

const measure = (layerRect: DOMRect, slot: HTMLElement | undefined): Geometry | null => {
  const slotRect = slot?.getBoundingClientRect();

  if (!slotRect || slotRect.width === 0 || slotRect.height === 0) {
    return null;
  }

  return {
    x: slotRect.left - layerRect.left,
    y: slotRect.top - layerRect.top,
    width: slotRect.width,
    height: slotRect.height,
  };
};

const applyGeometry = (host: HTMLElement, geometry: Geometry | null, interactive: boolean) => {
  if (!geometry) {
    host.style.visibility = 'hidden';
    host.style.pointerEvents = 'none';

    return;
  }

  host.style.visibility = 'visible';
  // Inline rather than through the resize guard's sx: an emotion class generated mid-drag never
  // reaches the PiP document, whose stylesheets were copied when it opened.
  host.style.pointerEvents = interactive ? 'auto' : 'none';
  host.style.transform = `translate3d(${geometry.x}px, ${geometry.y}px, 0)`;
  host.style.width = `${geometry.width}px`;
  host.style.height = `${geometry.height}px`;
};

/**
 * Sizes a host before its content exists, so the integrator's iframe is never created at the
 * host's empty intrinsic size and never loads believing its viewport is 32px across.
 *
 * A tile added while the grid is hidden — portrait fullscreen puts the whole tiles column in
 * `display: none` — has a slot that measures zero, so the room stands in as the size to boot at
 * until the real geometry arrives.
 */
export const placeHostBeforePaint = (slot: HTMLElement | undefined, host: HTMLElement) => {
  // The layer's own ref is not attached yet: React runs a child's layout effect before it binds
  // the parent's ref. The host is already inside the layer, so ask the DOM instead.
  const layer = host.parentElement;

  if (!layer) {
    return;
  }

  const layerRect = layer.getBoundingClientRect();
  const geometry = measure(layerRect, slot);

  if (geometry) {
    applyGeometry(host, geometry, true);

    return;
  }

  host.style.width = `${layerRect.width}px`;
  host.style.height = `${layerRect.height}px`;
};

/**
 * Sidebar resizes, pin/unpin and pagination move a slot without resizing it, which a
 * ResizeObserver never reports, so the position is polled instead.
 */
export const useCustomTileGeometrySync = (
  layerRef: RefObject<HTMLElement>,
  registry: CustomTileHostRegistry,
  resizing: boolean
) => {
  useEffect(() => {
    // The PiP layer lives in another document, whose window drives its own frames.
    const view = layerRef.current?.ownerDocument.defaultView ?? window;

    // Keyed by element, not by tile name: removing and re-adding a tile builds a fresh host that
    // starts hidden, and a name-keyed cache would recognise its geometry and skip the first write.
    const appliedKeys = new WeakMap<HTMLElement, string | null>();
    let frame = 0;

    const sync = () => {
      const layer = layerRef.current;

      if (!layer) {
        return;
      }

      const layerRect = layer.getBoundingClientRect();
      const measured: MeasuredHost[] = [];

      // Every rect is read before the first style is written, or the writes would force a
      // reflow between the reads.
      registry.hosts.forEach((host, name) => {
        measured.push({ host, geometry: measure(layerRect, registry.slots.get(name)) });
      });

      measured.forEach(({ host, geometry }) => {
        const key = toKey(geometry, !resizing);

        if (appliedKeys.has(host) && appliedKeys.get(host) === key) {
          return;
        }

        appliedKeys.set(host, key);
        applyGeometry(host, geometry, !resizing);
      });
    };

    const tick = () => {
      sync();
      frame = view.requestAnimationFrame(tick);
    };

    frame = view.requestAnimationFrame(tick);

    return () => view.cancelAnimationFrame(frame);
  }, [layerRef, registry, resizing]);
};
